import { MetadataAttachment } from '../GuardianChat.interface';

/**
 * A single chat message draft, scoped to one chat thread.
 *
 * Drafts are intentionally NOT persisted beyond the current browser/app/Teams
 * session: they live in memory and are mirrored to `sessionStorage`, which the
 * browser clears when the tab/session ends. They are additionally cleared on
 * logout (web) and on user switch (Teams).
 */
export interface ChatDraft {
  /** The unsent message text. */
  text: string;
  /** Whether the "important" toggle was enabled for the unsent message. */
  isImportant: boolean;
  /** A reference to an already-uploaded attachment, or null when none. */
  attachment: MetadataAttachment | null;
}

const STORAGE_KEY = 'mnspro-chat-drafts';

/**
 * Stable reference for "no draft". Returning the same frozen object keeps
 * `useSyncExternalStore` snapshots referentially equal so consumers do not
 * re-render on unrelated draft changes.
 */
export const EMPTY_CHAT_DRAFT: ChatDraft = Object.freeze({
  text: '',
  isImportant: false,
  attachment: null,
});

type Listener = () => void;

const listeners = new Set<Listener>();
let drafts = new Map<string, ChatDraft>();

function hasSessionStorage(): boolean {
  try {
    return typeof window !== 'undefined' && !!window.sessionStorage;
  } catch {
    return false;
  }
}

function persist(): void {
  if (!hasSessionStorage()) {
    return;
  }
  try {
    if (drafts.size === 0) {
      window.sessionStorage.removeItem(STORAGE_KEY);
    } else {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(Object.fromEntries(drafts)));
    }
  } catch {
    // Storage full or unavailable — drafts remain in memory only.
  }
}

function hydrate(): void {
  if (!hasSessionStorage()) {
    return;
  }
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return;
    }
    const parsed = JSON.parse(raw) as Record<string, ChatDraft>;
    drafts = new Map(Object.entries(parsed));
  } catch {
    drafts = new Map();
  }
}

hydrate();

function notify(): void {
  listeners.forEach((listener) => listener());
}

function isEmptyDraft(draft: ChatDraft): boolean {
  return draft.text.trim() === '' && !draft.isImportant && draft.attachment === null;
}

/** Returns the draft for a thread, or a stable empty draft when none exists. */
export function getChatDraft(threadId: string): ChatDraft {
  return drafts.get(threadId) ?? EMPTY_CHAT_DRAFT;
}

/**
 * Merges the given partial into the thread's draft. When the resulting draft is
 * empty (no text, not important, no attachment), the entry is removed entirely
 * so the chat list falls back to the real last message.
 */
export function patchChatDraft(threadId: string, partial: Partial<ChatDraft>): void {
  const current = drafts.get(threadId) ?? EMPTY_CHAT_DRAFT;
  const next: ChatDraft = { ...current, ...partial };

  if (isEmptyDraft(next)) {
    if (!drafts.has(threadId)) {
      return;
    }
    drafts.delete(threadId);
  } else {
    drafts.set(threadId, next);
  }

  persist();
  notify();
}

/** Removes the draft for a single thread. */
export function clearChatDraft(threadId: string): void {
  if (!drafts.delete(threadId)) {
    return;
  }
  persist();
  notify();
}

/** Removes all drafts. Called on logout (web) and on user switch (Teams). */
export function clearAllChatDrafts(): void {
  if (drafts.size === 0) {
    return;
  }
  drafts = new Map();
  persist();
  notify();
}

/** Subscribes to any draft change. Returns an unsubscribe function. */
export function subscribeChatDrafts(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
